import { useDisclosure } from '@mantine/hooks';
import { type SyntheticEvent, useEffect, useRef } from 'react';
import { StaticLocationContextProvider } from 'ts/base/hooks/UseLocation';
import { BaseProviders } from 'ts/base/ReactUtils';
import { useEventListener } from 'ts/commons/hooks/useEventListener';
import { MODAL_STORE } from 'ts/commons/modal/ModalManager.store';
import type { ModalOptions } from 'ts/commons/modal/ModalUtils';
import { Loader } from 'ts/components/Loader';
import { Modal } from 'ts/components/Modal';
import { useStore } from 'zustand';

/**
 * Component that subscribes to the modal store and renders all stored modals into a portal (body). This component
 * should be included once in the app root.
 */
export function ModalManager() {
	const modals = useStore(MODAL_STORE, state => state.modals);

	return (
		<>
			{modals.map(modal => (
				<ManagedModal key={modal.id} {...modal} />
			))}
		</>
	);
}

type ManagedModalProps = {
	id: string;
	options: ModalOptions;
};

function ManagedModal({ id, options }: ManagedModalProps) {
	const { onClose, contentRenderer, ...modalProps } = options;
	const removeModal = useStore(MODAL_STORE, state => state.removeModal);
	const [opened, { open, close }] = useDisclosure(false);
	const elementRef = useRef<HTMLDivElement>(null);

	// Open after first render to get the transition
	useEffect(open, [open]);

	const onCloseInternal = () => {
		close();
		onClose?.();
		// Remove from store delayed to get the transition
		setTimeout(() => removeModal(id), 300);
	};

	useEventListener(
		'keydown',
		event => {
			if (event.key !== 'Escape') {
				return;
			}
			const isInert =
				// Ensure no Base UI popover is being closed
				document.querySelector('[data-base-ui-portal]') != null ||
				// Ensure the Modal is the topmost overlay
				Array.from(
					document.querySelectorAll(
						'[data-mantine-shared-portal-node] .mantine-Modal-root,.mantine-Popover-dropdown:not([style*="display: none;"])'
					)
				).at(-1) !== elementRef.current;
			if (!isInert) {
				onCloseInternal();
			}
		},
		true
	);

	return (
		<Modal
			ref={elementRef}
			onClose={onCloseInternal}
			opened={opened}
			size="xl"
			closeOnClickOutside={false}
			closeOnEscape={false}
			onClick={(event: SyntheticEvent) => event.stopPropagation()}
			{...modalProps}
		>
			<StaticLocationContextProvider>
				<BaseProviders suspenseFallback={<Loader inline="centered" active />}>
					{contentRenderer(onCloseInternal)}
				</BaseProviders>
			</StaticLocationContextProvider>
		</Modal>
	);
}
